import { Box, ClickAwayListener, Modal } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSingleSelectContext } from '../../../../provider/SingleSelectProvider';
import { secondaryIconActive, secondaryIconHover, secondaryIconInactive } from '../../../../utils/colors';
import { isMUIIcon } from '../../../../utils/iconUtils';
import ImsIcon from '../../ImsIcon';

interface SingleSelectModalProps {
  open: boolean;
  onClose: () => void;
  sizes: any;
  buttonPosition?: DOMRect;
}

const SingleSelectModal: React.FC<SingleSelectModalProps> = ({ open, onClose, buttonPosition }) => {
  const intl = useIntl();
  const { currentlySelected, setCurrentlySelected, items } = useSingleSelectContext();

  const handleItemClicked = (item: any): void => {
    if (currentlySelected.id !== item.id) {
      setCurrentlySelected(item);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropComponent={() => null}
      disableEscapeKeyDown
      sx={{
        zIndex: 999,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 30}px` : '50%',
        width: 'fit-content',
        transform: 'none',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Title Section */}
            <Box
              sx={{
                padding: '0.3rem 1rem',
                borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.1rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.select' })}
              </Box>
            </Box>

            {/* Options List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {items.map((item, index) => (
                <Box
                  key={`selectItem-${index}`}
                  onClick={() => handleItemClicked(item)}
                  sx={{
                    display: 'grid',
                    gridTemplateRows: '2rem',
                    gridTemplateColumns: '28% 65%',
                    alignItems: 'center',
                    padding: '0.2rem 0.5rem',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    width: '12rem',
                    userSelect: 'none',
                    transition: 'background-color 0.2s ease-in-out',
                    color: currentlySelected.id === item.id ? secondaryIconActive : secondaryIconInactive,
                    '&:hover': {
                      bgcolor: secondaryIconHover,
                    },
                  }}
                >
                  {isMUIIcon(item.icon) ? (
                    <item.icon style={{ width: '2rem', height: '2rem', margin: '.1rem 0.6rem' }} />
                  ) : (
                    <ImsIcon Icon={item.icon} style={{ width: '2rem', height: '2rem', margin: '.1rem 0.6rem' }} />
                  )}
                  {item.text}
                </Box>
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default SingleSelectModal;
